import { Dialog } from "primereact/dialog";
import { useEffect, useRef, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { FiAlertCircle, FiArrowLeft, FiArrowRight, FiBookOpen, FiCheck, FiLink, FiLock } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import SocialPasswordConfirmation from "./SocialPasswordConfirmation";
import "./SocialEmailConflictDialog.css";

const getErrorMessage = (error) => {
	const data = error?.response?.data;
	const messages = data?.data;
	const firstMessage = Array.isArray(messages)
		? messages[0]
		: messages && typeof messages === "object" ? Object.values(messages).flat()[0] : null;
	return firstMessage || data?.message || "Unable to continue right now. Please try again.";
};

/** Connect an existing account or explicitly confirm creating a separate Google account. */
const SocialEmailConflictDialog = ({ visible, email, onHide, onLink, onSeparate, isSubmitting }) => {
	const [mode, setMode] = useState("choose");
	const [formError, setFormError] = useState("");
	const [pending, setPending] = useState(false);
	const stepFocusRef = useRef(null);
	const returnFocusRef = useRef(null);
	const busy = isSubmitting || pending;

	useEffect(() => {
		if (visible) returnFocusRef.current = document.activeElement;
		else {
			setMode("choose");
			setFormError("");
			returnFocusRef.current?.focus();
		}
	}, [visible]);

	useEffect(() => {
		if (visible) stepFocusRef.current?.focus();
	}, [mode, visible]);

	const changeMode = (nextMode) => {
		setFormError("");
		setMode(nextMode);
	};

	const resolve = async (action) => {
		setFormError("");
		setPending(true);
		try {
			await action();
			changeMode("choose");
		} catch (error) {
			setFormError(getErrorMessage(error));
		} finally {
			setPending(false);
		}
	};

	const titles = {
		choose: "Connect Google to Akello",
		link: "Confirm your Akello password",
		"separate-confirm": "Create a new account?",
	};
	const descriptions = {
		choose: "One more step. Choose how you’d like to continue.",
		link: "Confirm it’s you to connect Google to your existing account.",
		"separate-confirm": "You’ll start fresh with a separate Akello account using Google.",
	};

	return (
		<Dialog
			visible={visible}
			onHide={() => { if (!busy) onHide(); }}
			header={<><span className="social-connect__eyebrow" aria-hidden="true">GOOGLE SIGN-IN</span><span className="sr-only">{titles[mode]}</span></>}
			modal
			blockScroll
			draggable={false}
			resizable={false}
			closable={!busy}
			closeOnEscape={!busy}
			focusOnShow={false}
			onShow={() => stepFocusRef.current?.focus()}
			ariaCloseIconLabel="Close Google connection"
			className="social-email-conflict-dialog"
			maskClassName="social-connect-mask"
		>
			<div className="social-connect" aria-busy={busy}>
				<div className="social-connect__brands" aria-hidden="true">
					<span className="social-connect__brand"><FcGoogle size={26} /></span>
					<FiLink className="social-connect__link-icon" size={18} />
					<span className="social-connect__brand social-connect__brand--akello"><FiBookOpen size={26} /></span>
				</div>
				<h2 className="social-connect__title" ref={mode === "separate-confirm" ? stepFocusRef : null} tabIndex={-1}>{titles[mode]}</h2>
				<p className="social-connect__intro">{descriptions[mode]}</p>
				{email ? (
					<div className="social-connect__account">
						<FcGoogle size={20} aria-hidden="true" />
						<div><span className="social-connect__account-label">Google account</span><span className="social-connect__email">{email}</span></div>
					</div>
				) : null}

				{mode === "choose" ? (
					<>
						<section className="social-connect__recommended" aria-labelledby="social-connect-existing">
							<div className="social-connect__option-heading">
								<h3 id="social-connect-existing">Keep your existing account</h3>
								<span className="social-connect__badge">Recommended</span>
							</div>
							<p>Connect Google with your Akello password.</p>
							<ul className="social-connect__benefits">
								<li><FiCheck aria-hidden="true" /> Keep your books, subscriptions, and settings</li>
								<li><FiCheck aria-hidden="true" /> Sign in with Google next time</li>
							</ul>
							<button type="button" ref={stepFocusRef} disabled={busy} onClick={() => changeMode("link")} className="social-connect__button social-connect__button--primary">
								<FiLock aria-hidden="true" /><span>Connect my existing account</span><FiArrowRight aria-hidden="true" />
							</button>
						</section>
						<div className="social-connect__divider"><span>or start fresh</span></div>
						<button type="button" disabled={busy} onClick={() => changeMode("separate-confirm")} className="social-connect__button social-connect__button--secondary">
							<FcGoogle size={20} aria-hidden="true" /><span>Continue with Google only</span><FiArrowRight aria-hidden="true" />
						</button>
						<p className="social-connect__helper">Creates a new account. Your existing library won’t carry over.</p>
					</>
				) : null}

				{mode === "link" ? (
					<SocialPasswordConfirmation
						email={email}
						busy={busy}
						error={formError}
						inputRef={stepFocusRef}
						onSubmit={(values) => resolve(() => onLink({ email, password: values.password }))}
						onBack={() => changeMode("choose")}
					/>
				) : null}

				{mode === "separate-confirm" ? (
					<div className="social-connect__confirmation">
						<div className="social-connect__warning">
							<FiAlertCircle size={20} aria-hidden="true" />
							<div>
								<h3>The account we found stays as it is</h3>
								<p>Its books, subscriptions, and settings stay with it. It can still be opened with its username and password.</p>
								<p>The new account starts on its own, and this email will be used there instead.</p>
								<p>If the account we found is yours, go back and connect Google to it.</p>
							</div>
						</div>
						{formError ? <p className="social-connect__error" role="alert">{formError}</p> : null}
						<button type="button" disabled={busy} onClick={() => resolve(onSeparate)} className="social-connect__button social-connect__button--primary">
							{busy ? <AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" /> : <FcGoogle size={20} aria-hidden="true" />}
							<span>{busy ? "Creating your account…" : "Create new account with Google"}</span>
						</button>
						<button type="button" disabled={busy} onClick={() => changeMode("choose")} className="social-connect__back"><FiArrowLeft aria-hidden="true" /> Back to account options</button>
					</div>
				) : null}
				<p className="social-connect__security"><FiLock size={12} aria-hidden="true" /> Your Akello password is never shared with Google.</p>
			</div>
		</Dialog>
	);
};

export default SocialEmailConflictDialog;
